import api from './api';

export interface BillingSubscription {
  id: number;
  userId: number;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  plan: string;
  status: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BillingInvoiceItem {
  id: number;
  invoiceId: number;
  description: string;
  quantity: number;
  unitPriceCentsUsd: number;
  totalCentsUsd: number;
  taxCentsUsd: number;
  createdAt: string;
}

export interface BillingDocumentItem {
  id: number;
  documentId: number;
  description: string;
  quantity: number;
  unitPriceCentsUsd: number;
  totalCentsUsd: number;
  taxCentsUsd: number;
  createdAt: string;
}

export interface BillingDocument {
  id: number;
  userId: number;
  type: 'quote' | 'delivery' | 'invoice';
  status: string;
  documentNumber: string;
  title?: string;
  notes?: string;
  subtotalCentsUsd: number;
  taxCentsUsd: number;
  totalCentsUsd: number;
  issuedAt?: string;
  dueAt?: string;
  approvedAt?: string;
  approvedBy?: number;
  rejectedAt?: string;
  rejectionReason?: string;
  sourceDocumentId?: number;
  pdfPath?: string;
  reissueStatus?: string;
  reissueRequestedAt?: string;
  reissueReason?: string;
  reissuePayload?: {
    title?: string;
    notes?: string;
    dueAt?: string | null;
    items?: Array<{
      description: string;
      quantity: number;
      unitPriceCentsUsd: number;
      taxCentsUsd?: number;
    }>;
  };
  reissueApprovedAt?: string;
  reissueApprovedBy?: number;
  reissuedFromId?: number;
  createdAt: string;
  updatedAt: string;
  items?: BillingDocumentItem[];
  sourceDocument?: BillingDocument;
  user?: {
    id: number;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    organizationName?: string | null;
  };
}

export interface BillingInvoice {
  id: number;
  subscriptionId?: number;
  userId: number;
  stripeInvoiceId?: string;
  invoiceNumber: string;
  subtotalCentsUsd: number;
  taxCentsUsd: number;
  totalCentsUsd: number;
  status: string;
  issuedAt?: string;
  dueAt?: string;
  paidAt?: string;
  pdfPath?: string;
  createdAt: string;
  updatedAt: string;
  items?: BillingInvoiceItem[];
  subscription?: BillingSubscription;
}

export interface BillingInvoiceListParams {
  page?: number;
  limit?: number;
}

export interface BillingInvoiceListResponse {
  success: boolean;
  data: BillingInvoice[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface BillingDocumentListResponse {
  success: boolean;
  data: BillingDocument[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface BillingDocumentIssueActor {
  id: number;
  username: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

export interface BillingDocumentIssueHistoryItem {
  document: BillingDocument & {
    user?: {
      id: number;
      username: string;
      firstName: string | null;
      lastName: string | null;
      email: string | null;
      organizationName?: string | null;
    };
  };
  issuedBy: BillingDocumentIssueActor | null;
  issuedAction: string | null;
  issuedLogAt: string | null;
}

export interface BillingDocumentIssueHistoryResponse {
  success: boolean;
  data: BillingDocumentIssueHistoryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface BillingAdminUser {
  id: number;
  username: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  organizationName: string | null;
  createdAt: string;
}

export interface AdminSubscriptionRosterRow {
  userId: number;
  email: string;
  username: string;
  name: string;
  subscription: {
    id: number;
    status: string;
    plan: string;
    stripeSubscriptionId: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  override: {
    permanentlyFree: boolean;
    suspended: boolean;
    freeUntil: string | null;
  } | null;
}

export interface AdminSubscriptionConfig {
  stripeConfigured: boolean;
  defaultPriceId: string;
  billingGateEnabled: boolean;
}

export const billingService = {
  async getSubscriptions(): Promise<BillingSubscription[]> {
    const response = await api.get<{ success: boolean; data: BillingSubscription[] }>(
      '/billing/subscriptions'
    );
    return response.data.data;
  },

  async getInvoices(params?: BillingInvoiceListParams): Promise<BillingInvoiceListResponse> {
    const response = await api.get<BillingInvoiceListResponse>('/billing/invoices', {
      params,
    });
    return response.data;
  },

  async getInvoiceById(id: number): Promise<{ success: boolean; data: BillingInvoice }> {
    const response = await api.get<{ success: boolean; data: BillingInvoice }>(
      `/billing/invoices/${id}`
    );
    return response.data;
  },

  async downloadInvoicePdf(id: number): Promise<Blob> {
    const response = await api.get(`/billing/invoices/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async getDocuments(
    params?: BillingInvoiceListParams & { type?: string }
  ): Promise<BillingDocumentListResponse> {
    const response = await api.get<BillingDocumentListResponse>('/billing/documents', { params });
    return response.data;
  },

  async getDocumentIssueHistory(params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
    documentNumber?: string;
    recipientQuery?: string;
    issuedFrom?: string;
    issuedTo?: string;
    actorUserId?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<BillingDocumentIssueHistoryResponse> {
    const response = await api.get<BillingDocumentIssueHistoryResponse>(
      '/billing/admin/document-issue-history',
      {
        params,
      }
    );
    return response.data;
  },

  async downloadDocumentIssueHistoryCsv(params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
    documentNumber?: string;
    recipientQuery?: string;
    issuedFrom?: string;
    issuedTo?: string;
    actorUserId?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<Blob> {
    const response = await api.get('/billing/admin/document-issue-history/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  async getAdminSubscriptionConfig(): Promise<AdminSubscriptionConfig> {
    const response = await api.get<{ success: boolean; data: AdminSubscriptionConfig }>(
      '/billing/admin/subscriptions/config'
    );
    return response.data.data;
  },

  async listAdminSubscriptions(): Promise<AdminSubscriptionRosterRow[]> {
    const response = await api.get<{ success: boolean; data: AdminSubscriptionRosterRow[] }>(
      '/billing/admin/subscriptions'
    );
    return response.data.data;
  },

  async createAdminSubscription(payload: {
    userId: number;
    priceId?: string;
  }): Promise<{
    subscriptionId: number | null;
    stripeSubscriptionId: string;
    status: string;
    hostedInvoiceUrl: string | null;
  }> {
    const response = await api.post<{
      success: boolean;
      data: {
        subscriptionId: number | null;
        stripeSubscriptionId: string;
        status: string;
        hostedInvoiceUrl: string | null;
      };
    }>('/billing/admin/subscriptions', payload);
    return response.data.data;
  },

  async cancelAdminSubscription(stripeSubscriptionId: string): Promise<{
    stripeSubscriptionId: string;
    status: string;
    cancelAtPeriodEnd: boolean;
  }> {
    const response = await api.post<{
      success: boolean;
      data: { stripeSubscriptionId: string; status: string; cancelAtPeriodEnd: boolean };
    }>('/billing/admin/subscriptions/cancel', { stripeSubscriptionId });
    return response.data.data;
  },

  async getUsers(): Promise<{ success: boolean; data: BillingAdminUser[] }> {
    const response = await api.get<{ success: boolean; data: BillingAdminUser[] }>(
      '/billing/admin/users'
    );
    return response.data;
  },

  async getDocumentById(id: number): Promise<{ success: boolean; data: BillingDocument }> {
    const response = await api.get<{ success: boolean; data: BillingDocument }>(
      `/billing/documents/${id}`
    );
    return response.data;
  },

  async createDocument(payload: {
    type: 'quote' | 'delivery' | 'invoice';
    title?: string;
    notes?: string;
    dueAt?: string | null;
    items: Array<{
      description: string;
      quantity: number;
      unitPriceCentsUsd: number;
      taxCentsUsd?: number;
    }>;
    sourceDocumentId?: number | null;
  }): Promise<{ success: boolean; data: BillingDocument }> {
    const response = await api.post<{ success: boolean; data: BillingDocument }>(
      '/billing/documents',
      payload
    );
    return response.data;
  },

  async updateDocument(
    id: number,
    payload: {
      title?: string;
      notes?: string;
      dueAt?: string | null;
      items: Array<{
        description: string;
        quantity: number;
        unitPriceCentsUsd: number;
        taxCentsUsd?: number;
      }>;
    }
  ): Promise<{ success: boolean; data: BillingDocument }> {
    const response = await api.patch<{ success: boolean; data: BillingDocument }>(
      `/billing/documents/${id}`,
      payload
    );
    return response.data;
  },

  async submitDocumentForApproval(
    id: number
  ): Promise<{ success: boolean; data: BillingDocument }> {
    const response = await api.post<{ success: boolean; data: BillingDocument }>(
      `/billing/documents/${id}/submit-approval`
    );
    return response.data;
  },

  async approveDocument(id: number): Promise<{ success: boolean; data: BillingDocument }> {
    const response = await api.post<{ success: boolean; data: BillingDocument }>(
      `/billing/documents/${id}/approve`
    );
    return response.data;
  },

  async rejectDocument(
    id: number,
    reason: string
  ): Promise<{ success: boolean; data: BillingDocument }> {
    const response = await api.post<{ success: boolean; data: BillingDocument }>(
      `/billing/documents/${id}/reject`,
      { reason }
    );
    return response.data;
  },

  async issueDocument(id: number): Promise<{ success: boolean; data: BillingDocument }> {
    const response = await api.post<{ success: boolean; data: BillingDocument }>(
      `/billing/documents/${id}/issue`
    );
    return response.data;
  },

  async convertDocument(id: number): Promise<{ success: boolean; data: BillingDocument }> {
    const response = await api.post<{ success: boolean; data: BillingDocument }>(
      `/billing/documents/${id}/convert`
    );
    return response.data;
  },

  async downloadDocumentPdf(id: number): Promise<Blob> {
    const response = await api.get(`/billing/documents/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async resendDocument(id: number): Promise<{ success: boolean; data: { pdfPath: string } }> {
    const response = await api.post<{ success: boolean; data: { pdfPath: string } }>(
      `/billing/documents/${id}/resend`
    );
    return response.data;
  },

  async requestDocumentReissue(
    id: number,
    payload: {
      reason?: string;
      payload?: {
        title?: string;
        notes?: string;
        dueAt?: string | null;
        items?: Array<{
          description: string;
          quantity: number;
          unitPriceCentsUsd: number;
          taxCentsUsd?: number;
        }>;
      };
    }
  ): Promise<{ success: boolean; data: any }> {
    const response = await api.post<{ success: boolean; data: any }>(
      `/billing/documents/${id}/reissue-request`,
      payload
    );
    return response.data;
  },

  async approveDocumentReissue(id: number): Promise<{ success: boolean; data: BillingDocument }> {
    const response = await api.post<{ success: boolean; data: BillingDocument }>(
      `/billing/documents/${id}/reissue-approve`
    );
    return response.data;
  },

  async rejectDocumentReissue(
    id: number,
    reason: string
  ): Promise<{ success: boolean; data: BillingDocument }> {
    const response = await api.post<{ success: boolean; data: BillingDocument }>(
      `/billing/documents/${id}/reissue-reject`,
      { reason }
    );
    return response.data;
  },

  async voidDocument(id: number): Promise<{ success: boolean; data: BillingDocument }> {
    const response = await api.post<{ success: boolean; data: BillingDocument }>(
      `/billing/documents/${id}/void`
    );
    return response.data;
  },
};
